import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Form,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import {
  fetchFeedbackList,
  submitFeedback,
  type FeedbackItem,
} from "../services/compliance";
import { useUiState } from "../state";
import { textMap } from "../i18n";

const { TextArea } = Input;
const { Title, Paragraph } = Typography;

export function FeedbackPage() {
  const { lang } = useUiState();
  const t = textMap[lang];
  const [form] = Form.useForm<{
    category: string;
    subject: string;
    contact: string;
    question: string;
  }>();
  const [submitting, setSubmitting] = useState(false);
  const [tableLoading, setTableLoading] = useState(true);
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [appMessage, contextHolder] = message.useMessage();

  useEffect(() => {
    let mounted = true;
    void fetchFeedbackList()
      .then((rows) => {
        if (mounted) {
          setItems(rows);
        }
      })
      .finally(() => {
        if (mounted) {
          setTableLoading(false);
        }
      });
    return () => {
      mounted = false;
    };
  }, []);

  const statusText = useMemo(
    () => ({
      open: t.feedbackStatusOpen,
      reviewing: t.feedbackStatusReviewing,
      resolved: t.feedbackStatusResolved,
    }),
    [t.feedbackStatusOpen, t.feedbackStatusResolved, t.feedbackStatusReviewing],
  );

  const onSubmit = async (values: {
    category: string;
    subject: string;
    contact: string;
    question: string;
  }) => {
    setSubmitting(true);
    try {
      const composedQuestion = `[${values.category}] ${values.subject.trim()}\n${values.question.trim()}`;
      await submitFeedback(values.contact.trim(), composedQuestion);
      appMessage.success(t.feedbackSubmitSuccess);
      form.setFieldsValue({
        category: "bug",
        subject: "",
        contact: "",
        question: "",
      });
      setTableLoading(true);
      try {
        const rows = await fetchFeedbackList();
        setItems(rows);
      } finally {
        setTableLoading(false);
      }
    } catch (error) {
      appMessage.error(
        error instanceof Error && error.message
          ? error.message
          : lang === "zh"
            ? "提交失败，请稍后重试"
            : "Submit failed, please try again",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      {contextHolder}
      <div className="feedback-sentinel-hero">
        <Title level={2} className="feedback-sentinel-title">
          {lang === "zh" ? "发送反馈" : "Send Feedback"}
        </Title>
        <Paragraph className="feedback-sentinel-subtitle">
          {lang === "zh"
            ? "帮助我们持续改进合规审查体验。"
            : "Help us improve your compliance review experience."}
        </Paragraph>
      </div>

      <Card className="feedback-sentinel-form-card" bordered={false}>
        <Form
          form={form}
          layout="vertical"
          initialValues={{ category: "bug", subject: "", contact: "", question: "" }}
          onFinish={(values) => void onSubmit(values)}
        >
          <div className="feedback-grid">
            <Form.Item
              label={lang === "zh" ? "分类" : "Category"}
              name="category"
              rules={[{ required: true }]}
            >
              <Select
                options={[
                  { label: lang === "zh" ? "程序错误" : "Bug", value: "bug" },
                  { label: lang === "zh" ? "功能建议" : "Feature", value: "feature" },
                  {
                    label: lang === "zh" ? "合规准确性" : "Compliance Accuracy",
                    value: "accuracy",
                  },
                  { label: lang === "zh" ? "其他" : "Other", value: "other" },
                ]}
              />
            </Form.Item>
            <Form.Item
              label={lang === "zh" ? "主题" : "Subject"}
              name="subject"
              rules={[{ required: true, message: lang === "zh" ? "请填写主题" : "Please enter subject" }]}
            >
              <Input
                placeholder={
                  lang === "zh"
                    ? "请简要描述你的反馈主题"
                    : "Brief summary of your feedback"
                }
                maxLength={255}
              />
            </Form.Item>
          </div>
          <Form.Item
            label={t.feedbackContactLabel}
            name="contact"
            rules={[{ required: true, message: t.feedbackContactPlaceholder }]}
          >
            <Input placeholder={t.feedbackContactPlaceholder} maxLength={255} />
          </Form.Item>
          <Form.Item
            label={t.feedbackQuestionLabel}
            name="question"
            rules={[{ required: true, message: t.feedbackQuestionPlaceholder }]}
          >
            <TextArea
              rows={5}
              placeholder={t.feedbackQuestionPlaceholder}
              maxLength={3000}
            />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={submitting}>
            {t.feedbackSubmit}
          </Button>
        </Form>
      </Card>

      <div className="feedback-context-cards">
        <div className="feedback-context-item">
          <span className="feedback-context-icon">🔒</span>
          <div>
            <h4>{lang === "zh" ? "安全提交" : "Secure Submission"}</h4>
            <p>{lang === "zh" ? "您的反馈通过加密通道传输至合规团队。" : "Your feedback is transmitted over an encrypted channel to our compliance team."}</p>
          </div>
        </div>
        <div className="feedback-context-item">
          <span className="feedback-context-icon">⏱</span>
          <div>
            <h4>{lang === "zh" ? "响应时间" : "Response Time"}</h4>
            <p>{lang === "zh" ? "工程师通常会在 24-48 工作小时内回复关键问题。" : "Our engineers usually respond within 24-48 business hours for critical bugs."}</p>
          </div>
        </div>
        <div className="feedback-context-item">
          <span className="feedback-context-icon">✨</span>
          <div>
            <h4>{lang === "zh" ? "优先审核" : "Priority Review"}</h4>
            <p>{lang === "zh" ? "企业账户的功能请求将获得高优先级审核。" : "Enterprise accounts receive high-priority review for feature requests."}</p>
          </div>
        </div>
      </div>

      <Card className="glass-card" title={t.feedbackHistoryTitle}>
        <Table
          rowKey="id"
          size="small"
          loading={tableLoading}
          dataSource={items}
          locale={{ emptyText: t.feedbackEmpty }}
          pagination={{ pageSize: 10 }}
          columns={[
            {
              title: t.feedbackColCreated,
              dataIndex: "created_at",
              key: "created_at",
              width: 180,
              render: (value: string) =>
                new Date(value).toLocaleString(
                  lang === "zh" ? "zh-CN" : "en-US",
                ),
            },
            {
              title: t.feedbackColContact,
              dataIndex: "contact",
              key: "contact",
              width: 220,
            },
            {
              title: t.feedbackColQuestion,
              dataIndex: "question",
              key: "question",
            },
            {
              title: t.feedbackColStatus,
              dataIndex: "status",
              key: "status",
              width: 120,
              render: (status: FeedbackItem["status"]) => {
                const color =
                  status === "resolved"
                    ? "green"
                    : status === "reviewing"
                      ? "gold"
                      : "blue";
                return (
                  <Tag color={color}>
                    {statusText[status as "open" | "reviewing" | "resolved"] ??
                      status}
                  </Tag>
                );
              },
            },
          ]}
        />
      </Card>
    </Space>
  );
}
